/**
 * ReachabilityAnalyzer — closed-form jump-envelope reachability analysis.
 *
 * Analyzes a PlatformerLevel to determine which platforms are reachable
 * from spawn points, given a set of movement profiles. Catches the class
 * of bug where a platform is placed too high for the default jump with
 * no intermediate path — while respecting metroidvania gates (platforms
 * intentionally unreachable until an ability unlock).
 *
 * Design: `docs/design/systems/tooling/MAP_REACHABILITY_TOOLKIT.md` §1–3.
 *
 * v1 model (conservative, closed-form):
 * - Single-jump height: H₁ = jumpPower² / (2·gravity)
 * - Multi-jump height:  H_n = n · H₁ (optimistic upper bound)
 * - Airtime:            T = 2·jumpPower/gravity
 * - Horizontal reach to apex: R = moveSpeed · (jumpPower/gravity)
 * - Full traverse before landing: ≈ 2R
 * - No arc-occlusion (v2 if false-positives appear)
 * - No moving-platform simulation (treated as static at their origin)
 *
 * Coordinate convention (see docs/guides/LEVEL_FORMATS_EXPLAINED.md):
 * - Platform `y` is the TOP edge of the hitbox.
 * - The standing surface (where a mover lands) is `platform.y`.
 * - Up is negative-y in screen space, but for reachability we work in
 *   "height gain" = A.surfaceY - B.surfaceY (positive = B is higher).
 *   Since lower y = higher on screen, height gain = A.y - B.y.
 */

// ── Types ───────────────────────────────────────────────────────────

/**
 * A movement profile describing what a mover can do.
 * Used to compute the jump envelope for reachability edges.
 */
export interface MovementProfile {
  id: string;
  jumpPower: number;   // upward impulse px/s
  gravity: number;     // px/s²
  moveSpeed: number;   // horizontal px/s
  maxJumps: number;    // 1 = single, 2 = double, ...
  abilities?: string[]; // 'dash','blink','high_jump' — for gated matching
}

/**
 * The gate vocabulary. A platform with `reach.gate` declares it's
 * intentionally unreachable until the named ability is unlocked.
 *
 * - `double_jump` — requires maxJumps ≥ 2 (or an air-jump unlock)
 * - `dash`        — requires a horizontal dash ability
 * - `blink`       — requires a teleport/blink ability
 * - `high_jump`   — requires a jumpPower boost beyond base
 * - `decor`       — non-standable / background → excluded from the graph
 */
export type GateKind = 'double_jump' | 'dash' | 'blink' | 'high_jump' | 'decor';

/**
 * Platform-level reachability gate. Authored in the level JSON:
 * ```jsonc
 * { "reach": { "gate": "double_jump", "note": "secret — needs air-dash" } }
 * ```
 */
export interface ReachGate {
  gate: GateKind;
  note?: string;
}

/**
 * Verdict for a single platform.
 *
 * - `reachable`      — reached by the base profile flood ✅
 * - `gated-ok`       — NOT reachable by base, IS by union, AND has a `reach.gate` tag 🟡
 * - `gated-untagged` — NOT reachable by base, IS by union, but NO `reach` tag 🟠
 * - `orphan`         — NOT reachable by ANY profile (including all abilities) 🔴
 * - `decor`          — excluded from analysis (reach.gate = 'decor') — not standable
 * - `spawn`          — this is a spawn platform (always reachable by definition)
 */
export type Verdict = 'reachable' | 'gated-ok' | 'gated-untagged' | 'orphan' | 'decor' | 'spawn';

export interface PlatformVerdict {
  platformId: string;
  verdict: Verdict;
  /** Nearest reachable platform (for 🟠/🔴 — tells the author where to add a stepping stone). */
  nearestReachableId?: string;
  /** Vertical gap to the nearest reachable platform (px). Positive = platform is higher. */
  deltaY?: number;
  /** Horizontal gap to the nearest reachable platform (px). */
  deltaX?: number;
  /** How much the vertical gap exceeds the base profile's max jump height (px). Negative = within reach. */
  verticalOverage?: number;
  /** The gate declared on this platform, if any. */
  gate?: GateKind;
  /** Author note on the gate, if any. */
  gateNote?: string;
}

export interface ReachabilityReport {
  levelId: string;
  levelName: string;
  platformCount: number;
  spawnCount: number;
  verdicts: PlatformVerdict[];
  summary: {
    reachable: number;
    gatedOk: number;
    gatedUntagged: number;
    orphan: number;
    decor: number;
    spawn: number;
  };
  /** The base profile used for the primary flood. */
  baseProfileId: string;
  /** The union profile used for the "can anything reach it" check. */
  unionProfileId: string;
}

// ── Objective reachability (§4) ─────────────────────────────────────

/**
 * An interactable objective in the level — something the player must be
 * able to reach to use (teleporter, vendor, quest NPC, loot spawn, etc.).
 *
 * In the current level data, the only interactables stored in JSON are
 * teleporter platforms (type='teleporter'). NPCs/vendors are spawned
 * dynamically by the server and aren't in the level JSON.
 */
export type ObjectiveKind = 'teleporter' | 'vendor' | 'quest_npc' | 'loot' | 'shrine' | 'unknown';

export interface Objective {
  id: string;
  kind: ObjectiveKind;
  x: number;
  y: number;
  /** The platform ID this objective sits on, if determinable. */
  platformId?: string;
}

export interface ObjectiveVerdict {
  objectiveId: string;
  kind: ObjectiveKind;
  verdict: 'reachable' | 'unreachable';
  /** The platform the objective sits on. */
  platformId?: string;
  /** Nearest reachable platform (if unreachable). */
  nearestReachableId?: string;
  deltaY?: number;
  deltaX?: number;
}

export interface ObjectiveReachabilityReport {
  levelId: string;
  levelName: string;
  objectiveCount: number;
  verdicts: ObjectiveVerdict[];
  summary: {
    reachable: number;
    unreachable: number;
  };
}

// ── Canonical profiles ──────────────────────────────────────────────

/**
 * The BASE movement profile — the weakest movement we promise every
 * player has at the zone. Defaults to Paladin jumpPower (1000, the
 * lowest) + double-jump (maxJumps=2, base movement per CharacterFactory).
 *
 * This is a policy constant, not magic. See MAP_REACHABILITY_TOOLKIT.md §5.
 */
export const BASE_MOVEMENT_PROFILE: MovementProfile = {
  id: 'base',
  jumpPower: 1000,   // Paladin (weakest class jump)
  gravity: 1500,     // physics.config.ts default
  moveSpeed: 260,    // Paladin (slowest class)
  maxJumps: 2,       // CharacterFactory default
  abilities: [],
};

/**
 * The UNION movement profile — the strongest movement any player could
 * have with all abilities unlocked. Used to distinguish "gated"
 * (reachable with upgrades) from "orphan" (unreachable by anyone).
 *
 * Uses Rogue jumpPower (1250, highest) + double-jump + dash + blink.
 */
export const UNION_MOVEMENT_PROFILE: MovementProfile = {
  id: 'union',
  jumpPower: 1250,   // Rogue (highest class jump)
  gravity: 1500,
  moveSpeed: 380,    // Rogue (fastest class)
  maxJumps: 2,
  abilities: ['dash', 'blink', 'high_jump'],
};

// ── Jump envelope math (closed-form) ────────────────────────────────

/**
 * Single-jump apex height: H₁ = jumpPower² / (2·gravity).
 * Kinematic: v² = v₀² - 2·g·d; at apex v=0 → d = v₀²/(2g).
 */
export function singleJumpHeight(profile: MovementProfile): number {
  return (profile.jumpPower * profile.jumpPower) / (2 * profile.gravity);
}

/**
 * Multi-jump apex height (optimistic upper bound): H_n ≈ n · H₁.
 * Assumes each jump fires near the previous apex (maximally efficient).
 */
export function multiJumpHeight(profile: MovementProfile): number {
  return profile.maxJumps * singleJumpHeight(profile);
}

/**
 * Horizontal reach to apex: R = moveSpeed · (jumpPower / gravity).
 * This is the horizontal distance traveled during the rise to apex.
 */
export function horizontalReachToApex(profile: MovementProfile): number {
  return profile.moveSpeed * (profile.jumpPower / profile.gravity);
}

/**
 * Full horizontal traverse before landing, scaling with maxJumps.
 *
 * With n jumps (each fired at the previous apex), the mover stays airborne
 * for (n+1)·(jumpPower/gravity) seconds: n rise phases + 1 final fall phase.
 * Horizontal reach = moveSpeed · (n+1) · (jumpPower/gravity) = (maxJumps+1) · R.
 *
 * For maxJumps=1: 2R (single jump arc — rise + fall).
 * For maxJumps=2: 3R (rise → apex-jump → rise → fall).
 *
 * This is the optimistic upper bound (assumes each jump fired at apex, no
 * input latency, no air friction). v1 uses this as the can-it-possibly-reach
 * bound; a v2 conservative check would use 2R (single-jump-only) as the
 * guaranteed lower bound.
 */
export function fullHorizontalReach(profile: MovementProfile): number {
  return (profile.maxJumps + 1) * horizontalReachToApex(profile);
}

// ── Platform surface geometry ───────────────────────────────────────

/**
 * A platform's standing surface: the top edge (where a mover lands)
 * and its horizontal x-span.
 *
 * Per the coordinate convention, `platform.y` IS the top edge.
 * The standing surface Y = platform.y.
 */
interface PlatformSurface {
  id: string;
  /** Top edge (standing surface) — lower y = higher on screen. */
  surfaceY: number;
  /** Left edge. */
  xLeft: number;
  /** Right edge. */
  xRight: number;
  /** Original platform index (for traceability). */
  index: number;
  /** Reach gate, if declared. */
  gate?: GateKind;
  gateNote?: string;
}

/**
 * Extract the standing surface from a platform object.
 * Handles both Platform interface and raw JSON objects.
 */
function platformSurface(platform: any, index: number): PlatformSurface | null {
  if (!platform || typeof platform.x !== 'number' || typeof platform.y !== 'number') {
    return null;
  }
  const width = typeof platform.width === 'number' ? platform.width : 0;
  let gate: GateKind | undefined;
  let gateNote: string | undefined;
  if (platform.reach && typeof platform.reach.gate === 'string') {
    gate = platform.reach.gate as GateKind;
    gateNote = platform.reach.note;
  }
  return {
    id: platform.id || `plat_${index}`,
    surfaceY: platform.y,
    xLeft: platform.x,
    xRight: platform.x + width,
    index,
    gate,
    gateNote,
  };
}

/**
 * Horizontal gap between two platform x-spans. Returns 0 if they overlap.
 * This is the "clamp-distance" — the minimum horizontal distance a mover
 * must travel to get from one platform's x-span to the other's.
 */
function horizontalGap(a: PlatformSurface, b: PlatformSurface): number {
  if (a.xRight >= b.xLeft && b.xRight >= a.xLeft) {
    return 0; // Overlapping in x
  }
  if (a.xRight < b.xLeft) {
    return b.xLeft - a.xRight;
  }
  return a.xLeft - b.xRight;
}

/**
 * Vertical gain needed to go from platform A to platform B.
 * Positive = B is higher on screen (lower y). Negative = B is lower (drop-down).
 */
function verticalGain(a: PlatformSurface, b: PlatformSurface): number {
  return a.surfaceY - b.surfaceY;
}

// ── Reachable-edge test ─────────────────────────────────────────────

/**
 * Is platform B reachable from platform A given the movement profile?
 *
 * v1: closed-form envelope, no arc-occlusion.
 * - Upward: Δy ≤ H_n(profile) AND Δx ≤ 2R(profile)
 * - Downward (drop-down): always reachable if Δx ≤ 2R(profile) (gravity is free)
 */
function isReachable(from: PlatformSurface, to: PlatformSurface, profile: MovementProfile): boolean {
  const dx = horizontalGap(from, to);
  const dy = verticalGain(from, to); // positive = to is higher

  const maxHorizontal = fullHorizontalReach(profile);

  // Must be within horizontal reach
  if (dx > maxHorizontal) return false;

  // Drop-down: gravity is free, any downward distance is reachable
  if (dy <= 0) return true;

  // Upward: must be within multi-jump height
  const maxVertical = multiJumpHeight(profile);
  return dy <= maxVertical;
}

// ── Flood fill ──────────────────────────────────────────────────────

/**
 * Find which platform a spawn point stands on.
 * A spawn stands on the platform whose top surface is at or just below
 * the spawn point, with x-overlap.
 */
function findSpawnPlatform(
  spawn: { x: number; y: number },
  surfaces: PlatformSurface[],
): PlatformSurface | null {
  let best: PlatformSurface | null = null;
  let bestY = Infinity;

  for (const s of surfaces) {
    // Spawn x must be within the platform's x-span
    if (spawn.x < s.xLeft || spawn.x > s.xRight) continue;
    // Platform must be at or below the spawn (surfaceY >= spawn.y means
    // the platform top is below the spawn point on screen)
    if (s.surfaceY < spawn.y - 5) continue; // 5px tolerance
    // Pick the highest platform that's still below (closest surface)
    if (s.surfaceY < bestY) {
      bestY = s.surfaceY;
      best = s;
    }
  }

  return best;
}

/**
 * Flood-fill the reachability graph from a set of seed platforms.
 * Returns the set of reachable platform indices.
 */
function floodReachable(
  surfaces: PlatformSurface[],
  seeds: Set<number>,
  profile: MovementProfile,
): Set<number> {
  const reachable = new Set<number>(seeds);
  const queue: number[] = Array.from(seeds);

  while (queue.length > 0) {
    const currentIdx = queue.shift()!;
    const current = surfaces[currentIdx];

    for (let i = 0; i < surfaces.length; i++) {
      if (reachable.has(i)) continue;
      const target = surfaces[i];

      // Bidirectional: can we reach it from current?
      if (isReachable(current, target, profile)) {
        reachable.add(i);
        queue.push(i);
        continue;
      }
      // Or can current be reached FROM target? (target might be higher,
      // but if we can drop down from target to current, then target is
      // reachable IF target itself is reachable from something else.
      // This is handled by the flood — we don't need to add it here.)
    }
  }

  return reachable;
}

// ── Main analyzer ───────────────────────────────────────────────────

/**
 * Analyze reachability of a level's platforms.
 *
 * @param level A PlatformerLevel (or any object with `platforms` and `spawnPoints`).
 *   If a GridLevel is passed, convert it first via LevelFormatConverter.
 * @param baseProfile The weakest movement profile (default: BASE_MOVEMENT_PROFILE).
 * @param unionProfile The strongest movement profile (default: UNION_MOVEMENT_PROFILE).
 * @returns A ReachabilityReport with per-platform verdicts.
 */
export function analyzeReachability(
  level: any,
  baseProfile: MovementProfile = BASE_MOVEMENT_PROFILE,
  unionProfile: MovementProfile = UNION_MOVEMENT_PROFILE,
): ReachabilityReport {
  const levelId = level?.id || 'unknown';
  const levelName = level?.name || 'unknown';

  // Extract platforms — handle both PlatformerLevel and raw JSON
  const rawPlatforms: any[] = Array.isArray(level?.platforms) ? level.platforms : [];

  // Extract spawn points
  const rawSpawns: any[] = Array.isArray(level?.spawnPoints) ? level.spawnPoints : [];

  // Build platform surfaces, filtering out decor-gated platforms
  const surfaces: PlatformSurface[] = [];
  const decorIndices = new Set<number>();

  for (let i = 0; i < rawPlatforms.length; i++) {
    const p = rawPlatforms[i];
    const surface = platformSurface(p, i);
    if (!surface) continue;

    if (surface.gate === 'decor') {
      decorIndices.add(i);
      continue;
    }

    surfaces.push({ ...surface, index: surfaces.length });
  }

  // Map original platform indices to surfaces-array indices for decor tracking
  // (We rebuild the index mapping since we filtered out decor platforms)

  // Find spawn platforms
  const spawnSurfaceIndices = new Set<number>();
  for (const spawn of rawSpawns) {
    if (typeof spawn?.x !== 'number' || typeof spawn?.y !== 'number') continue;
    const surface = findSpawnPlatform(spawn, surfaces);
    if (surface) {
      spawnSurfaceIndices.add(surface.index);
    }
  }

  // Flood from spawns using base profile
  const baseReachable = floodReachable(surfaces, spawnSurfaceIndices, baseProfile);

  // Flood from spawns using union profile (all abilities)
  const unionReachable = floodReachable(surfaces, spawnSurfaceIndices, unionProfile);

  // Classify each platform
  const verdicts: PlatformVerdict[] = [];
  let reachableCount = 0;
  let gatedOkCount = 0;
  let gatedUntaggedCount = 0;
  let orphanCount = 0;
  const decorCount = decorIndices.size;
  let spawnCount = spawnSurfaceIndices.size;

  for (const surface of surfaces) {
    const isSpawn = spawnSurfaceIndices.has(surface.index);
    const isBaseReachable = baseReachable.has(surface.index);
    const isUnionReachable = unionReachable.has(surface.index);

    let verdict: Verdict;
    let nearestReachableId: string | undefined;
    let deltaY: number | undefined;
    let deltaX: number | undefined;
    let verticalOverage: number | undefined;

    if (isSpawn) {
      verdict = 'spawn';
      spawnCount++;
    } else if (isBaseReachable) {
      verdict = 'reachable';
      reachableCount++;
    } else if (isUnionReachable) {
      // Reachable by union but not base → it's gated
      if (surface.gate && surface.gate !== 'decor') {
        verdict = 'gated-ok';
        gatedOkCount++;
      } else {
        verdict = 'gated-untagged';
        gatedUntaggedCount++;

        // Find nearest reachable platform for the author
        const nearest = findNearestReachable(surface, baseReachable, surfaces);
        if (nearest) {
          nearestReachableId = nearest.id;
          deltaY = verticalGain(nearest, surface); // positive = surface is higher
          deltaX = horizontalGap(nearest, surface);
          const maxJump = multiJumpHeight(baseProfile);
          verticalOverage = deltaY > 0 ? deltaY - maxJump : undefined;
        }
      }
    } else {
      // Not reachable by any profile → orphan
      verdict = 'orphan';
      orphanCount++;

      // Find nearest reachable platform (from union set — anything at all)
      const nearest = findNearestReachable(surface, unionReachable, surfaces);
      if (nearest) {
        nearestReachableId = nearest.id;
        deltaY = verticalGain(nearest, surface);
        deltaX = horizontalGap(nearest, surface);
        const maxJump = multiJumpHeight(unionProfile);
        verticalOverage = deltaY > 0 ? deltaY - maxJump : undefined;
      }
    }

    verdicts.push({
      platformId: surface.id,
      verdict,
      nearestReachableId,
      deltaY,
      deltaX,
      verticalOverage,
      gate: surface.gate,
      gateNote: surface.gateNote,
    });
  }

  // Adjust spawnCount (we double-counted above)
  spawnCount = spawnSurfaceIndices.size;

  return {
    levelId,
    levelName,
    platformCount: rawPlatforms.length,
    spawnCount: rawSpawns.length,
    verdicts,
    summary: {
      reachable: reachableCount,
      gatedOk: gatedOkCount,
      gatedUntagged: gatedUntaggedCount,
      orphan: orphanCount,
      decor: decorCount,
      spawn: spawnCount,
    },
    baseProfileId: baseProfile.id,
    unionProfileId: unionProfile.id,
  };
}

/**
 * Find the nearest reachable platform (by Euclidean distance of the gap)
 * to a target surface.
 */
function findNearestReachable(
  target: PlatformSurface,
  reachableSet: Set<number>,
  surfaces: PlatformSurface[],
): PlatformSurface | null {
  let best: PlatformSurface | null = null;
  let bestDist = Infinity;

  for (const idx of reachableSet) {
    if (idx === target.index) continue;
    const candidate = surfaces[idx];
    const dx = horizontalGap(target, candidate);
    const dy = Math.abs(verticalGain(target, candidate));
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < bestDist) {
      bestDist = dist;
      best = candidate;
    }
  }

  return best;
}

// ── Unknown gate validation ─────────────────────────────────────────

const KNOWN_GATES = new Set<GateKind>(['double_jump', 'dash', 'blink', 'high_jump', 'decor']);

/**
 * Check a platform's `reach.gate` value. Returns an error message if
 * the gate is not in the known vocabulary, null if valid.
 */
export function validateReachGate(platform: any): string | null {
  if (!platform?.reach?.gate) return null;
  const gate = platform.reach.gate;
  if (typeof gate !== 'string') return `reach.gate must be a string, got ${typeof gate}`;
  if (!KNOWN_GATES.has(gate as GateKind)) {
    return `Unknown reach.gate "${gate}" — valid gates: ${Array.from(KNOWN_GATES).join(', ')}`;
  }
  return null;
}

// ── Objective reachability analysis (§4) ────────────────────────────

/**
 * Extract interactable objectives from a level.
 *
 * Currently identifies:
 * - Teleporter platforms (type='teleporter') — the only interactables
 *   stored in level JSON. NPCs/vendors are spawned dynamically.
 *
 * Extensible: add extraction for vendor/quest_npc/loot/shrine when those
 * are stored in level JSON (currently they're server-spawned).
 */
export function extractObjectives(level: any): Objective[] {
  const objectives: Objective[] = [];
  const platforms: any[] = Array.isArray(level?.platforms) ? level.platforms : [];

  for (let i = 0; i < platforms.length; i++) {
    const p = platforms[i];
    if (!p || typeof p.x !== 'number') continue;

    if (p.type === 'teleporter') {
      objectives.push({
        id: p.id || `tp_${i}`,
        kind: 'teleporter',
        x: p.x,
        y: p.y,
        platformId: p.id,
      });
    }
  }

  return objectives;
}

/**
 * Analyze whether every interactable objective in a level sits on a
 * base-reachable platform. An unreachable quest-giver = a dead map.
 *
 * Reuses the reachability graph from analyzeReachability — runs the base
 * flood, then checks each objective's host platform is in the reachable set.
 *
 * @param level A PlatformerLevel (or any object with `platforms` and `spawnPoints`).
 * @param baseProfile The weakest movement profile (default: BASE_MOVEMENT_PROFILE).
 * @returns An ObjectiveReachabilityReport with per-objective verdicts.
 */
export function analyzeObjectiveReachability(
  level: any,
  baseProfile: MovementProfile = BASE_MOVEMENT_PROFILE,
): ObjectiveReachabilityReport {
  const levelId = level?.id || 'unknown';
  const levelName = level?.name || 'unknown';

  // Get the platform reachability report (reuses the flood)
  const reachReport = analyzeReachability(level, baseProfile, UNION_MOVEMENT_PROFILE);

  // Build a set of reachable platform IDs from the base flood
  const reachablePlatformIds = new Set<string>();
  for (const v of reachReport.verdicts) {
    if (v.verdict === 'reachable' || v.verdict === 'spawn') {
      reachablePlatformIds.add(v.platformId);
    }
  }

  // Extract objectives
  const objectives = extractObjectives(level);

  // Classify each objective
  const verdicts: ObjectiveVerdict[] = [];
  let reachableCount = 0;
  let unreachableCount = 0;

  for (const obj of objectives) {
    const isReachable = obj.platformId ? reachablePlatformIds.has(obj.platformId) : false;

    if (isReachable) {
      verdicts.push({
        objectiveId: obj.id,
        kind: obj.kind,
        verdict: 'reachable',
        platformId: obj.platformId,
      });
      reachableCount++;
    } else {
      // Find nearest reachable platform for the author
      const surfaces = buildSurfaces(level);
      const objSurface = findSpawnPlatform(obj, surfaces);
      let nearestId: string | undefined;
      let deltaY: number | undefined;
      let deltaX: number | undefined;

      if (objSurface) {
        const reachableSurfaces = surfaces.filter((_s, i) => {
          const v = reachReport.verdicts[i];
          return v && (v.verdict === 'reachable' || v.verdict === 'spawn');
        });
        let bestDist = Infinity;
        let best: PlatformSurface | null = null;
        for (const candidate of reachableSurfaces) {
          const dx = horizontalGap(objSurface, candidate);
          const dy = Math.abs(verticalGain(objSurface, candidate));
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < bestDist) {
            bestDist = dist;
            best = candidate;
          }
        }
        if (best) {
          nearestId = best.id;
          deltaY = verticalGain(best, objSurface);
          deltaX = horizontalGap(best, objSurface);
        }
      }

      verdicts.push({
        objectiveId: obj.id,
        kind: obj.kind,
        verdict: 'unreachable',
        platformId: obj.platformId,
        nearestReachableId: nearestId,
        deltaY,
        deltaX,
      });
      unreachableCount++;
    }
  }

  return {
    levelId,
    levelName,
    objectiveCount: objectives.length,
    verdicts,
    summary: {
      reachable: reachableCount,
      unreachable: unreachableCount,
    },
  };
}

/**
 * Build platform surfaces from a level (internal helper for objective analysis).
 * Filters out decor-gated platforms.
 */
function buildSurfaces(level: any): PlatformSurface[] {
  const rawPlatforms: any[] = Array.isArray(level?.platforms) ? level.platforms : [];
  const surfaces: PlatformSurface[] = [];
  for (let i = 0; i < rawPlatforms.length; i++) {
    const p = rawPlatforms[i];
    const surface = platformSurface(p, i);
    if (!surface) continue;
    if (surface.gate === 'decor') continue;
    surfaces.push({ ...surface, index: surfaces.length });
  }
  return surfaces;
}

// ── Soft-lock detection (§4) ────────────────────────────────────────

/**
 * Soft-lock verdict for a single platform.
 *
 * - `safe`       — the platform has at least one outgoing edge (can leave)
 * - `soft-lock`  — reachable but NO outgoing edge to any other platform
 *                  (the inverse of an orphan: you can get IN but can't get OUT)
 *
 * A platform with zero outgoing edges by the union profile (strongest
 * movement) is a true trap — no class/ability can escape. We check with
 * union so we only flag genuine dead-ends, not "base can't climb back
 * but dash can" (that's a one-way design call, surfaced separately).
 *
 * Exception: if the platform is the ONLY reachable platform in the map,
 * it's not a soft-lock (it's just a tiny map with nowhere else to go).
 */
export type SoftLockVerdict = 'safe' | 'soft-lock';

export interface SoftLockPlatformVerdict {
  platformId: string;
  verdict: SoftLockVerdict;
  /** Number of outgoing edges from this platform (to any other non-decor platform). */
  outgoingEdges: number;
}

export interface SoftLockReport {
  levelId: string;
  levelName: string;
  platformCount: number;
  verdicts: SoftLockPlatformVerdict[];
  summary: {
    safe: number;
    softLock: number;
  };
}

/**
 * Analyze a level for soft-locks: platforms you can reach but never leave.
 *
 * Reuses the reachability graph — a platform is a soft-lock if it's
 * reachable (by base OR union flood) AND has zero outgoing edges to any
 * other non-decor platform under the union profile (the strongest
 * movement, so we only flag genuine no-escape traps).
 *
 * @param level A PlatformerLevel (or any object with `platforms` and `spawnPoints`).
 * @param baseProfile The weakest movement profile (default: BASE_MOVEMENT_PROFILE).
 * @param unionProfile The strongest movement profile (default: UNION_MOVEMENT_PROFILE).
 */
export function analyzeSoftLocks(
  level: any,
  baseProfile: MovementProfile = BASE_MOVEMENT_PROFILE,
  unionProfile: MovementProfile = UNION_MOVEMENT_PROFILE,
): SoftLockReport {
  const levelId = level?.id || 'unknown';
  const levelName = level?.name || 'unknown';

  const surfaces = buildSurfaces(level);
  const rawSpawns: any[] = Array.isArray(level?.spawnPoints) ? level.spawnPoints : [];

  // Seed = spawn platforms (union of base + union floods — anything reachable at all)
  const spawnSurfaceIndices = new Set<number>();
  for (const spawn of rawSpawns) {
    if (typeof spawn?.x !== 'number' || typeof spawn?.y !== 'number') continue;
    const surface = findSpawnPlatform(spawn, surfaces);
    if (surface) spawnSurfaceIndices.add(surface.index);
  }

  const baseReachable = floodReachable(surfaces, spawnSurfaceIndices, baseProfile);
  const unionReachable = floodReachable(surfaces, spawnSurfaceIndices, unionProfile);

  // Union of all reachable platforms (anything a player could stand on)
  const allReachable = new Set<number>([...baseReachable, ...unionReachable]);

  // If only one platform is reachable, none can be a soft-lock (tiny map)
  const reachableCount = allReachable.size;

  const verdicts: SoftLockPlatformVerdict[] = [];
  let safeCount = 0;
  let softLockCount = 0;

  for (const surface of surfaces) {
    if (!allReachable.has(surface.index)) {
      // Unreachable platforms are not soft-lock candidates (they're orphans/gated)
      continue;
    }

    // Count outgoing edges from this platform under the union profile
    let outgoingEdges = 0;
    for (let i = 0; i < surfaces.length; i++) {
      if (i === surface.index) continue;
      if (isReachable(surface, surfaces[i], unionProfile)) {
        outgoingEdges++;
      }
    }

    // Soft-lock: reachable, zero outgoing edges, AND there are other
    // reachable platforms (otherwise it's just a 1-platform map)
    const isSoftLock = outgoingEdges === 0 && reachableCount > 1;

    const verdict: SoftLockVerdict = isSoftLock ? 'soft-lock' : 'safe';
    if (isSoftLock) {
      softLockCount++;
    } else {
      safeCount++;
    }

    verdicts.push({
      platformId: surface.id,
      verdict,
      outgoingEdges,
    });
  }

  return {
    levelId,
    levelName,
    platformCount: surfaces.length,
    verdicts,
    summary: {
      safe: safeCount,
      softLock: softLockCount,
    },
  };
}

// ── Spawn-safety analysis (§4) ──────────────────────────────────────

/**
 * Mover bbox constants for spawn-safety. The player is 40×40 (see
 * CLAUDE.md "the player's `y - 40` pattern works only because the player
 * happens to be 40×40"). Spawn `y` is the TOP of the hitbox (coordinate
 * convention); feet are at `spawn.y + MOVER_HEIGHT`.
 *
 * SNAP_BUFFER matches PHYSICS.logic.groundSnapBuffer (20px) — the
 * tolerance validateOnGround uses when raycasting for ground.
 *
 * The max fall distance for the no-ground check mirrors MapSystem:
 * `metadata.spawnSnapMaxFallPx` on the level (per-map opt-in). When set,
 * spawns farther than that from a platform are "in the void". When NOT
 * set, MapSystem.validateOnGround uses Infinity — the spawn snaps to any
 * platform below regardless of distance (aerial drop-in spawns like the
 * lobby's). The analyzer defaults to Infinity to match; a map that sets
 * the opt-in gets the stricter check.
 */
const MOVER_HEIGHT = 40;
const SNAP_BUFFER = 20;

/**
 * Spawn-safety verdict for a single spawn point.
 *
 * - `safe`          — stands on solid ground, not buried, has headroom, in-bounds
 * - `no-ground`     — no platform surface within fall distance below the spawn
 * - `buried`        — spawn point is inside a platform body (below the top surface)
 * - `no-headroom`   — a platform above intrudes into the player's head zone
 * - `out-of-bounds` — spawn x/y is outside the level bounds
 */
export type SpawnSafetyVerdict =
  | 'safe'
  | 'no-ground'
  | 'buried'
  | 'no-headroom'
  | 'out-of-bounds';

export interface SpawnSafetyVerdictEntry {
  /** Index in the spawnPoints array. */
  index: number;
  x: number;
  y: number;
  verdict: SpawnSafetyVerdict;
  /** The platform the spawn lands on (ground), if found. */
  groundPlatformId?: string;
  /** The platform intruding into headroom, if any. */
  blockingPlatformId?: string;
  /** Human-readable detail about the issue. */
  detail?: string;
}

export interface SpawnSafetyReport {
  levelId: string;
  levelName: string;
  spawnCount: number;
  verdicts: SpawnSafetyVerdictEntry[];
  summary: {
    safe: number;
    noGround: number;
    buried: number;
    noHeadroom: number;
    outOfBounds: number;
  };
}

/**
 * A platform's full geometry (top + bottom), for spawn-safety checks.
 * Decor platforms are excluded (non-standable, but also non-blocking for
 * headroom purposes — they're visual only).
 */
interface PlatformGeometry {
  id: string;
  xLeft: number;
  xRight: number;
  topY: number;
  bottomY: number;
  gate?: GateKind;
}

function buildPlatformGeometries(level: any): PlatformGeometry[] {
  const rawPlatforms: any[] = Array.isArray(level?.platforms) ? level.platforms : [];
  const geos: PlatformGeometry[] = [];
  for (let i = 0; i < rawPlatforms.length; i++) {
    const p = rawPlatforms[i];
    if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') continue;
    const width = typeof p.width === 'number' ? p.width : 0;
    const height = typeof p.height === 'number' ? p.height : 0;
    let gate: GateKind | undefined;
    if (p.reach && typeof p.reach.gate === 'string') {
      gate = p.reach.gate as GateKind;
    }
    geos.push({
      id: p.id || `plat_${i}`,
      xLeft: p.x,
      xRight: p.x + width,
      topY: p.y,
      bottomY: p.y + height,
      gate,
    });
  }
  return geos;
}

/**
 * Analyze spawn-point safety for a level.
 *
 * Checks each spawn point for:
 * 1. **Out-of-bounds** — spawn x/y outside level width/height.
 * 2. **No-ground** — no platform surface within MAX_SPAWN_FALL_DISTANCE below.
 * 3. **Buried** — spawn point is inside a platform body (below the top surface,
 *    above the bottom). validateOnGround only snaps DOWN, so a buried spawn
 *    stays buried — the player spawns inside solid geometry.
 * 4. **No-headroom** — after snapping to ground, a platform above intrudes
 *    into the player's head zone (the 40px above the standing surface).
 *
 * @param level A PlatformerLevel (or any object with `platforms`, `spawnPoints`, `width`, `height`).
 */
export function analyzeSpawnSafety(level: any): SpawnSafetyReport {
  const levelId = level?.id || 'unknown';
  const levelName = level?.name || 'unknown';
  const levelWidth = typeof level?.width === 'number' ? level.width : Infinity;
  const levelHeight = typeof level?.height === 'number' ? level.height : Infinity;

  // Per-map opt-in: metadata.spawnSnapMaxFallPx. When set, spawns farther
  // than this from a platform are "in the void". When NOT set, the game
  // snaps to any platform below (Infinity) — aerial drop-in spawns.
  const metadataMaxFall = (level as any)?.metadata?.spawnSnapMaxFallPx;
  const maxFallDistance = typeof metadataMaxFall === 'number' ? metadataMaxFall : Infinity;

  const geos = buildPlatformGeometries(level);
  // Exclude decor platforms from blocking/ground checks (visual only)
  const standable = geos.filter(g => g.gate !== 'decor');

  const rawSpawns: any[] = Array.isArray(level?.spawnPoints) ? level.spawnPoints : [];

  const verdicts: SpawnSafetyVerdictEntry[] = [];
  let safe = 0, noGround = 0, buried = 0, noHeadroom = 0, outOfBounds = 0;

  for (let i = 0; i < rawSpawns.length; i++) {
    const sp = rawSpawns[i];
    if (!sp || typeof sp.x !== 'number' || typeof sp.y !== 'number') {
      verdicts.push({
        index: i, x: sp?.x ?? 0, y: sp?.y ?? 0,
        verdict: 'out-of-bounds',
        detail: 'Spawn point missing x/y coordinates',
      });
      outOfBounds++;
      continue;
    }

    // 1. Out-of-bounds
    if (sp.x < 0 || sp.x > levelWidth || sp.y < 0 || sp.y > levelHeight) {
      verdicts.push({
        index: i, x: sp.x, y: sp.y,
        verdict: 'out-of-bounds',
        detail: `Spawn at (${sp.x}, ${sp.y}) outside level bounds (${levelWidth}×${levelHeight})`,
      });
      outOfBounds++;
      continue;
    }

    // Platforms whose x-span contains the spawn
    const xOverlapping = standable.filter(g => sp.x >= g.xLeft && sp.x <= g.xRight);

    // 2. Buried: spawn is inside a platform body
    //    Platform top is above spawn (g.topY < sp.y - buffer) AND
    //    platform bottom is below spawn (g.bottomY > sp.y)
    const buriedIn = xOverlapping.find(
      g => g.topY < sp.y - SNAP_BUFFER && g.bottomY > sp.y,
    );
    if (buriedIn) {
      verdicts.push({
        index: i, x: sp.x, y: sp.y,
        verdict: 'buried',
        groundPlatformId: buriedIn.id,
        detail: `Spawn inside platform "${buriedIn.id}" (top=${buriedIn.topY}, bottom=${buriedIn.bottomY}, spawn.y=${sp.y})`,
      });
      buried++;
      continue;
    }

    // 3. No-ground: no platform surface within fall distance below
    //    Surface must be at or below the spawn's feet (sp.y + MOVER_HEIGHT),
    //    within SNAP_BUFFER tolerance above (validateOnGround snaps slightly
    //    above the surface), and within maxFallDistance below.
    //    maxFallDistance defaults to Infinity (aerial drop-in spawns are
    //    safe if ANY platform is below); maps that set
    //    metadata.spawnSnapMaxFallPx get the stricter check.
    const feetY = sp.y + MOVER_HEIGHT;
    const groundCandidates = xOverlapping
      .filter(g => g.topY >= sp.y - SNAP_BUFFER && g.topY <= feetY + maxFallDistance)
      .sort((a, b) => a.topY - b.topY); // nearest surface first (lowest y)

    if (groundCandidates.length === 0) {
      const fallDesc = maxFallDistance === Infinity
        ? 'No platform below spawn at all'
        : `No platform within ${maxFallDistance}px below spawn`;
      verdicts.push({
        index: i, x: sp.x, y: sp.y,
        verdict: 'no-ground',
        detail: `${fallDesc} at (${sp.x}, ${sp.y})`,
      });
      noGround++;
      continue;
    }

    const ground = groundCandidates[0];
    const surfaceY = ground.topY;

    // 4. No-headroom: a platform above intrudes into the player's head zone.
    //    Player stands with feet at surfaceY, head at surfaceY - MOVER_HEIGHT.
    //    A platform above (g.topY < surfaceY) whose bottom edge (g.bottomY)
    //    extends below the player's head (g.bottomY > surfaceY - MOVER_HEIGHT)
    //    blocks headroom. Exclude the ground platform itself.
    const blocking = xOverlapping.find(
      g => g.id !== ground.id &&
        g.topY < surfaceY &&
        g.bottomY > surfaceY - MOVER_HEIGHT,
    );
    if (blocking) {
      verdicts.push({
        index: i, x: sp.x, y: sp.y,
        verdict: 'no-headroom',
        groundPlatformId: ground.id,
        blockingPlatformId: blocking.id,
        detail: `Platform "${blocking.id}" (bottom=${blocking.bottomY}) intrudes into headroom above ground "${ground.id}" (surface=${surfaceY}, head=${surfaceY - MOVER_HEIGHT})`,
      });
      noHeadroom++;
      continue;
    }

    // All checks passed
    verdicts.push({
      index: i, x: sp.x, y: sp.y,
      verdict: 'safe',
      groundPlatformId: ground.id,
    });
    safe++;
  }

  return {
    levelId,
    levelName,
    spawnCount: rawSpawns.length,
    verdicts,
    summary: {
      safe,
      noGround,
      buried,
      noHeadroom,
      outOfBounds,
    },
  };
}

// ── Coverage map (§4) ───────────────────────────────────────────────

/**
 * Coverage verdict thresholds (heuristic, named not magic):
 * - `high`   ≥ 80% — most of the walkable space is reachable from spawn
 * - `medium` 50–80% — a meaningful chunk is gated/off-path
 * - `low`    < 50% — the map looks big but plays tiny
 */
const COVERAGE_HIGH_THRESHOLD = 80;
const COVERAGE_LOW_THRESHOLD = 50;
export type CoverageVerdict = 'high' | 'medium' | 'low';

export interface CoveragePlatformEntry {
  platformId: string;
  /** Platform surface width (px) — the walkable contribution. */
  width: number;
  /** Whether this platform is reachable from spawn by the base profile. */
  reachable: boolean;
}

export interface CoverageReport {
  levelId: string;
  levelName: string;
  /** Total walkable surface width across all non-decor platforms (px). */
  totalWalkableWidth: number;
  /** Walkable surface width reachable from spawn by the base profile (px). */
  reachableWalkableWidth: number;
  /** Reachable / total × 100, rounded to 1 decimal. */
  coveragePercent: number;
  /** Heuristic verdict from the thresholds above. */
  verdict: CoverageVerdict;
  /** Per-platform contribution. */
  platforms: CoveragePlatformEntry[];
}

/**
 * Analyze the coverage of a level — what fraction of the total walkable
 * surface area is reachable from spawn by the base profile.
 *
 * "Walkable space" = the sum of platform surface widths (platforms are
 * 1D standing surfaces; a platform of width W contributes W walkable px).
 * "Reachable area" = the subset of that surface reachable from spawn by
 * the base profile flood. Low coverage = a map that looks big but plays
 * tiny (most of the geometry is gated or off-path).
 *
 * Reuses the reachability flood from analyzeReachability.
 *
 * @param level A PlatformerLevel (or any object with `platforms` and `spawnPoints`).
 * @param baseProfile The weakest movement profile (default: BASE_MOVEMENT_PROFILE).
 */
export function analyzeCoverage(
  level: any,
  baseProfile: MovementProfile = BASE_MOVEMENT_PROFILE,
): CoverageReport {
  const levelId = level?.id || 'unknown';
  const levelName = level?.name || 'unknown';

  const surfaces = buildSurfaces(level);
  const rawSpawns: any[] = Array.isArray(level?.spawnPoints) ? level.spawnPoints : [];

  const spawnSurfaceIndices = new Set<number>();
  for (const spawn of rawSpawns) {
    if (typeof spawn?.x !== 'number' || typeof spawn?.y !== 'number') continue;
    const surface = findSpawnPlatform(spawn, surfaces);
    if (surface) spawnSurfaceIndices.add(surface.index);
  }

  const baseReachable = floodReachable(surfaces, spawnSurfaceIndices, baseProfile);

  let totalWalkableWidth = 0;
  let reachableWalkableWidth = 0;
  const platforms: CoveragePlatformEntry[] = [];

  for (const surface of surfaces) {
    // Surface width = xRight - xLeft (the walkable span)
    const width = surface.xRight - surface.xLeft;
    const isReachableFromSpawn = baseReachable.has(surface.index);
    totalWalkableWidth += width;
    if (isReachableFromSpawn) reachableWalkableWidth += width;
    platforms.push({
      platformId: surface.id,
      width,
      reachable: isReachableFromSpawn,
    });
  }

  const coveragePercent = totalWalkableWidth > 0
    ? Math.round((reachableWalkableWidth / totalWalkableWidth) * 1000) / 10
    : 0;

  const verdict: CoverageVerdict =
    coveragePercent >= COVERAGE_HIGH_THRESHOLD ? 'high' :
      coveragePercent >= COVERAGE_LOW_THRESHOLD ? 'medium' : 'low';

  return {
    levelId,
    levelName,
    totalWalkableWidth,
    reachableWalkableWidth,
    coveragePercent,
    verdict,
    platforms,
  };
}

// ── Fall-death audit (§4) ───────────────────────────────────────────

/**
 * The death line offset below world height (px). Matches
 * `safetySystem.config.ts` `worldBounds.deathLineYOffset = 2000`.
 * A platform below `worldHeight + this offset` is within the map;
 * below that is the void where SafetyMonitor rescues.
 */
const FALL_DEATH_LINE_OFFSET = 2000;

/**
 * Fall-death verdict for a single gap between adjacent platforms.
 *
 * - `safe`          — gap is within reach (crossable by base profile)
 * - `intended-pit`  — gap exceeds reach BUT a platform below catches the
 *                     fall (a deliberate one-way drop / pit level)
 * - `death-gap`     — gap exceeds reach AND no platform below — the player
 *                     falls into the void past the death line (SafetyMonitor
 *                     rescues, but it's an authoring error, not a design call)
 */
export type FallDeathVerdict = 'safe' | 'intended-pit' | 'death-gap';

export interface FallDeathGapEntry {
  /** The platform on the left side of the gap. */
  leftPlatformId: string;
  /** The platform on the right side of the gap. */
  rightPlatformId: string;
  /** Gap width in px (horizontal distance between the two platform x-spans). */
  gapWidth: number;
  /** Whether a platform below the gap catches the fall (safety net). */
  hasSafetyNet: boolean;
  /** The safety-net platform ID, if one exists. */
  safetyNetId?: string;
  verdict: FallDeathVerdict;
}

export interface FallDeathReport {
  levelId: string;
  levelName: string;
  /** Number of horizontal gaps between adjacent platforms. */
  gapCount: number;
  verdicts: FallDeathGapEntry[];
  summary: {
    safe: number;
    intendedPit: number;
    deathGap: number;
  };
}

/**
 * Analyze a level for fall-death gaps: horizontal gaps between platforms
 * that are too wide to cross and have no safety net below.
 *
 * The death line is `worldHeight + deathLineYOffset` (default offset 2000,
 * from `safetySystem.config.ts`). A gap over the void (no platform below
 * within the map) is a death-gap — the player falls past the death line
 * and SafetyMonitor rescues them (an authoring error, not a design call).
 *
 * A gap with a platform below (safety net) is an intended-pit — a
 * deliberate one-way drop. The player can't cross the gap by jumping but
 * can drop down to the lower platform. This is informational (design call).
 *
 * Uses the base profile's `fullHorizontalReach` (3R for double-jump) as
 * the "can cross" threshold. Gaps ≤ 3R are safe; gaps > 3R are pits.
 *
 * @param level A PlatformerLevel (or any object with `platforms` and `spawnPoints`).
 * @param baseProfile The weakest movement profile (default: BASE_MOVEMENT_PROFILE).
 */
export function analyzeFallDeath(
  level: any,
  baseProfile: MovementProfile = BASE_MOVEMENT_PROFILE,
): FallDeathReport {
  const levelId = level?.id || 'unknown';
  const levelName = level?.name || 'unknown';
  const levelHeight = typeof level?.height === 'number' ? level.height : Infinity;

  // Match the player's collision contract, not the broader reachability
  // graph. Teleporters and other sensors have geometry for interaction and
  // objective attribution, but CollisionResolver deliberately lets players
  // pass through them. Treating those contained sensor rectangles as
  // standable gap boundaries manufactures pits beside otherwise continuous
  // floors.
  const collisionPlatforms = (Array.isArray(level?.platforms) ? level.platforms : [])
    .filter((platform: any) => ![
      'rope',
      'teleporter',
      'cover',
      'decorative',
    ].includes(platform?.type));
  const surfaces = buildSurfaces({ ...level, platforms: collisionPlatforms });
  const maxReach = fullHorizontalReach(baseProfile);
  const maxTierDiff = 2 * multiJumpHeight(baseProfile);
  const maxTerraceStep = singleJumpHeight(baseProfile);

  // Merge overlapping/touching platforms at the SAME surface level into
  // continuous walkable spans. Gaps only exist between merged spans —
  // overlapping platforms (e.g. floor_1 at x=0..1500 and floor_2 at
  // x=1300..2300, both at y=1000) form a single continuous surface with
  // no gap. Platforms at different y (even within jump height) are NOT
  // merged — they're different walkable surfaces the player can jump
  // between, not a continuous floor.
  interface MergedSpan {
    xLeft: number;
    xRight: number;
    surfaceY: number;
    leftId: string;  // ID of the leftmost platform in this span
    rightId: string; // ID of the rightmost platform in this span
  }

  const sorted = [...surfaces].sort((a, b) => a.xLeft - b.xLeft);
  const spans: MergedSpan[] = [];

  for (const s of sorted) {
    // Find an existing span at the SAME surface level (within snap buffer)
    // that overlaps or touches this platform
    const mergeable = spans.find(span =>
      Math.abs(span.surfaceY - s.surfaceY) <= SNAP_BUFFER &&
      s.xLeft <= span.xRight
    );

    if (mergeable) {
      mergeable.xRight = Math.max(mergeable.xRight, s.xRight);
      mergeable.rightId = s.id;
    } else {
      spans.push({
        xLeft: s.xLeft,
        xRight: s.xRight,
        surfaceY: s.surfaceY,
        leftId: s.id,
        rightId: s.id,
      });
    }
  }

  // Sort merged spans by xLeft for gap-finding
  spans.sort((a, b) => a.xLeft - b.xLeft);

  const verdicts: FallDeathGapEntry[] = [];
  let safe = 0, intendedPit = 0, deathGap = 0;
  const deathLineY = levelHeight + FALL_DEATH_LINE_OFFSET;

  for (let i = 0; i < spans.length; i++) {
    const left = spans[i];

    // An elevated shelf can sort after a lower terrace that began farther
    // left. If that lower terrace covers the shelf's right edge and continues
    // beyond it, stepping off the shelf lands on collision terrain rather
    // than entering a horizontal void. Do not pair the shelf with a farther
    // platform merely because the catching terrace appeared earlier in the
    // x-sorted list; the catching terrace will receive its own gap analysis.
    const hasProjectedCatch = spans.some(candidate =>
      candidate !== left &&
      candidate.xLeft <= left.xLeft &&
      candidate.xRight > left.xRight &&
      candidate.surfaceY > left.surfaceY + SNAP_BUFFER &&
      candidate.surfaceY <= deathLineY
    );

    if (hasProjectedCatch) continue;

    // Find the nearest span to the right at roughly the same vertical level
    // (within 2x jump height). A touching/overlapping span that extends the
    // route and is within the weakest player's single-jump height is an
    // adjacent terrace, even when its top-Y differs. Stop at that terrace
    // instead of skipping it and pairing `left` with a nonadjacent platform
    // farther across the map. A much lower pit floor is intentionally not a
    // terrace here; it remains eligible as the safety net below a real gap.
    let right: MergedSpan | null = null;
    for (let j = i + 1; j < spans.length; j++) {
      const candidate = spans[j];
      if (Math.abs(left.surfaceY - candidate.surfaceY) > maxTierDiff) continue;

      // Fully contained spans do not advance the horizontal boundary.
      if (candidate.xRight <= left.xRight) continue;

      if (candidate.xLeft <= left.xRight) {
        if (Math.abs(left.surfaceY - candidate.surfaceY) <= maxTerraceStep) {
          right = null;
          break;
        }
        continue;
      }

      right = candidate;
      break;
    }

    if (!right) continue;

    const gapWidth = right.xLeft - left.xRight;
    if (gapWidth <= 0) continue;

    // Is the gap crossable by the base profile?
    if (gapWidth <= maxReach) {
      safe++;
      verdicts.push({
        leftPlatformId: left.rightId,
        rightPlatformId: right.leftId,
        gapWidth,
        hasSafetyNet: false,
        verdict: 'safe',
      });
      continue;
    }

    // Gap exceeds reach — check for a safety net below.
    // A safety net is any platform whose x-span covers the gap center,
    // at a lower y (below the gap), and within the map bounds (above
    // the death line).
    const gapCenterX = (left.xRight + right.xLeft) / 2;
    const gapY = Math.max(left.surfaceY, right.surfaceY);
    let safetyNet: PlatformSurface | null = null;
    for (const s of surfaces) {
      // Must cover the gap center horizontally
      if (gapCenterX < s.xLeft || gapCenterX > s.xRight) continue;
      // Must be below the gap (higher surfaceY = lower on screen)
      if (s.surfaceY <= gapY + SNAP_BUFFER) continue;
      // Must be above the death line (within the map)
      if (s.surfaceY > deathLineY) continue;
      // Pick the highest platform below the gap (closest = the net)
      if (!safetyNet || s.surfaceY < safetyNet.surfaceY) {
        safetyNet = s;
      }
    }

    if (safetyNet) {
      intendedPit++;
      verdicts.push({
        leftPlatformId: left.rightId,
        rightPlatformId: right.leftId,
        gapWidth,
        hasSafetyNet: true,
        safetyNetId: safetyNet.id,
        verdict: 'intended-pit',
      });
    } else {
      deathGap++;
      verdicts.push({
        leftPlatformId: left.rightId,
        rightPlatformId: right.leftId,
        gapWidth,
        hasSafetyNet: false,
        verdict: 'death-gap',
      });
    }
  }

  return {
    levelId,
    levelName,
    gapCount: verdicts.length,
    verdicts,
    summary: {
      safe,
      intendedPit,
      deathGap,
    },
  };
}

// ── Camera-fit analysis (§4) ────────────────────────────────────────

/**
 * The camera's "intended frame" at zoom 1.0 — the letterboxed world frame
 * the camera renders. Matches the `1280×720` default noted in
 * `src/game/effects/managers/viewport.ts` (the camera's `width/height`
 * default). This is the MapleStory-style fixed-zoom viewport band.
 *
 * At zoom 1.0, the camera shows a 1280×720 window that follows the player.
 * Geometry outside the level bounds is "off the visible band" — the camera
 * would render it but it's outside the intended map area (an authoring error).
 */
const CAMERA_VIEWPORT_WIDTH = 1280;
const CAMERA_VIEWPORT_HEIGHT = 720;

/**
 * Tolerance for platforms extending slightly beyond level bounds (px).
 * Floor platforms are typically 400px tall, placed at y = levelHeight − 200,
 * extending 200px below the visible map bottom so the player never sees the
 * floor's edge. Some maps also extend floors ~100px beyond the left/right
 * edges for the same reason. This is a standard authoring pattern, not an
 * out-of-bounds error. Platforms within this tolerance of the bounds are
 * "in-bounds"; beyond it is a real authoring error.
 */
const CAMERA_BOUNDS_TOLERANCE = 250;

/**
 * Camera-fit verdict for a single platform.
 *
 * - `in-bounds`    — platform geometry is fully within level bounds
 * - `out-of-bounds` — platform extends beyond level bounds (x<0, xRight>width,
 *                    y<0, yBottom>height) — the camera renders it but it's
 *                    outside the intended map area
 */
export type CameraFitVerdict = 'in-bounds' | 'out-of-bounds';

export interface CameraFitPlatformEntry {
  platformId: string;
  verdict: CameraFitVerdict;
  /** Which edge(s) are exceeded: 'left' | 'right' | 'top' | 'bottom'. */
  violations: ('left' | 'right' | 'top' | 'bottom')[];
}

export interface CameraFitReport {
  levelId: string;
  levelName: string;
  /** Level width (px). */
  levelWidth: number;
  /** Level height (px). */
  levelHeight: number;
  /** Camera viewport width at zoom 1.0 (px). */
  viewportWidth: number;
  /** Camera viewport height at zoom 1.0 (px). */
  viewportHeight: number;
  /** Vertical span of all platform surfaces (max surfaceY - min surfaceY). */
  verticalSpan: number;
  /** Whether the full vertical play area fits in one viewport at zoom 1.0. */
  fitsVertically: boolean;
  /** Whether the full horizontal play area fits in one viewport at zoom 1.0. */
  fitsHorizontally: boolean;
  /** Per-platform in-bounds / out-of-bounds verdicts. */
  platforms: CameraFitPlatformEntry[];
  summary: {
    inBounds: number;
    outOfBounds: number;
  };
}

/**
 * Analyze whether a level's geometry fits the camera's fixed-zoom viewport.
 *
 * Two checks:
 * 1. **Out-of-bounds geometry** — platforms whose x/y extends beyond level
 *    bounds (x<0, xRight>width, y<0, yBottom>height). The camera renders
 *    these but they're outside the intended map area — an authoring error.
 * 2. **Viewport fit** (informational) — does the full vertical/horizontal
 *    span of platform surfaces fit within one 1280×720 viewport at zoom 1.0?
 *    Most platformer maps are larger than the viewport (scrolling is normal),
 *    so this is informational, not a flag.
 *
 * Decor platforms are checked too — even visual-only geometry should be
 * within bounds (a decor platform outside the map is a rendering waste).
 *
 * @param level A PlatformerLevel (or any object with `platforms`, `width`, `height`).
 */
export function analyzeCameraFit(level: any): CameraFitReport {
  const levelId = level?.id || 'unknown';
  const levelName = level?.name || 'unknown';
  const levelWidth = typeof level?.width === 'number' ? level.width : Infinity;
  const levelHeight = typeof level?.height === 'number' ? level.height : Infinity;

  const geos = buildPlatformGeometries(level);

  const platforms: CameraFitPlatformEntry[] = [];
  let inBounds = 0, outOfBounds = 0;

  for (const g of geos) {
    const violations: ('left' | 'right' | 'top' | 'bottom')[] = [];
    if (g.xLeft < -CAMERA_BOUNDS_TOLERANCE) violations.push('left');
    if (g.xRight > levelWidth + CAMERA_BOUNDS_TOLERANCE) violations.push('right');
    if (g.topY < -CAMERA_BOUNDS_TOLERANCE) violations.push('top');
    if (g.bottomY > levelHeight + CAMERA_BOUNDS_TOLERANCE) violations.push('bottom');

    const verdict: CameraFitVerdict = violations.length > 0 ? 'out-of-bounds' : 'in-bounds';
    if (verdict === 'in-bounds') inBounds++;
    else outOfBounds++;

    platforms.push({
      platformId: g.id,
      verdict,
      violations,
    });
  }

  // Vertical/horizontal span of all platform surfaces (excluding decor)
  const standable = geos.filter(g => g.gate !== 'decor');
  let verticalSpan = 0;
  let fitsVertically = false;
  let fitsHorizontally = false;

  if (standable.length > 0) {
    const minSurfaceY = Math.min(...standable.map(g => g.topY));
    const maxSurfaceY = Math.max(...standable.map(g => g.topY));
    verticalSpan = maxSurfaceY - minSurfaceY;
    fitsVertically = verticalSpan <= CAMERA_VIEWPORT_HEIGHT;
    const minXLeft = Math.min(...standable.map(g => g.xLeft));
    const maxXRight = Math.max(...standable.map(g => g.xRight));
    const horizontalSpan = maxXRight - minXLeft;
    fitsHorizontally = horizontalSpan <= CAMERA_VIEWPORT_WIDTH;
  }

  return {
    levelId,
    levelName,
    levelWidth,
    levelHeight,
    viewportWidth: CAMERA_VIEWPORT_WIDTH,
    viewportHeight: CAMERA_VIEWPORT_HEIGHT,
    verticalSpan,
    fitsVertically,
    fitsHorizontally,
    platforms,
    summary: {
      inBounds,
      outOfBounds,
    },
  };
}
